/**
 * ResetPasswordPage - Set new password after clicking reset link.
 *
 * Features:
 * - Password input with confirmation
 * - Password strength indicator
 * - Uses token from URL query parameter
 * - Checks expires parameter to show expired screen early
 * - Reason parameter controls copy (setup, expired, reset)
 * - Success message with link to login
 * - Error handling for expired/invalid links
 */

import { useState, useMemo, type FormEvent } from 'react'
import { useSearch, Link } from '@tanstack/react-router'
import { Input } from '@/common/components/input'
import { Button } from '@/common/components/button'
import { Text } from '@/common/components/text'
import { PasswordStrengthIndicator } from '@/common/components/password-strength-indicator'
import { resetPassword } from '@/common/services/auth'
import { getPasswordStrength } from '@/common/utils/passwordStrength'
import * as styles from './reset-password.css'
import * as authStyles from './login.css'

export const Route = createFileRoute({
  component: ResetPasswordPage,
})

/**
 * Reason for password change - determines copy shown to user
 * - setup: First time password setup (admin created account)
 * - expired: Password has expired
 * - reset: Forgot password flow (default)
 */
type PasswordChangeReason = 'setup' | 'expired' | 'reset'

const COPY = {
  setup: {
    title: 'Create Your Password',
    description: 'Create a password to complete your account setup.',
    button: 'Create Password',
    submitting: 'Creating...',
    success: 'Your password has been created successfully.',
  },
  expired: {
    title: 'Update Your Password',
    description: 'Your password has expired. Please create a new password to continue.',
    button: 'Update Password',
    submitting: 'Updating...',
    success: 'Your password has been updated successfully.',
  },
  reset: {
    title: 'Reset Your Password',
    description: 'Enter your new password below.',
    button: 'Reset Password',
    submitting: 'Resetting...',
    success: 'Your password has been reset successfully.',
  },
} as const

function ResetPasswordPage() {
  const search = useSearch({ strict: false }) as { token?: string; reason?: string; expires?: string }
  const token = search.token || ''
  const reasonParam = search.reason || 'reset'
  const expiresParam = search.expires

  // Validate reason param, default to 'reset'
  const reason: PasswordChangeReason = useMemo(() => {
    if (reasonParam === 'setup' || reasonParam === 'expired') {
      return reasonParam
    }
    return 'reset'
  }, [reasonParam])

  // Check if token is expired based on expires parameter
  const isTokenExpired = useMemo(() => {
    if (!expiresParam) return false // No expires param means we can't check (legacy links)
    const expiresAt = parseInt(expiresParam, 10)
    if (isNaN(expiresAt)) return false
    return Date.now() / 1000 > expiresAt
  }, [expiresParam])

  const copy = COPY[reason]

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)

  // Password strength calculation
  const passwordStrength = useMemo(
    () => getPasswordStrength(password),
    [password]
  )

  // Validation
  const passwordsMatch = password === confirmPassword
  const isPasswordStrong = passwordStrength.score >= 2
  const isValid = password && confirmPassword && passwordsMatch && isPasswordStrong

  // Check if we have a valid token
  if (!token) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={authStyles.logo}
          />

          <div className={authStyles.form}>
            <Text variant="bodySm" color="error" align="center" className={authStyles.error}>
              Invalid password reset link. Please request a new one.
            </Text>

            <Link to="/forgot-password">
              <Button type="button" width="full">
                Request New Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Check if token is expired
  if (isTokenExpired) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={authStyles.logo}
          />

          <div className={authStyles.form}>
            <Text variant="bodyLg" weight="bold" align="center" className={authStyles.title}>
              Link Expired
            </Text>
            <Text variant="bodySm" color="muted" align="center" className={authStyles.description}>
              This password reset link has expired. Please request a new one.
            </Text>

            <Link to="/forgot-password">
              <Button type="button" width="full">
                Request New Link
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match
    if (!passwordsMatch) {
      setError('Passwords do not match')
      return
    }

    // Validate password strength
    if (!isPasswordStrong) {
      setError('Password is not strong enough')
      return
    }

    setIsSubmitting(true)

    try {
      await resetPassword(token, password)
      setIsSuccess(true)
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Failed to reset password. The link may have expired.')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isSuccess) {
    return (
      <div className={authStyles.container}>
        <div className={authStyles.card}>
          <img
            src="/images/orchard-offers.svg"
            alt="Orchard Cash Offer"
            className={authStyles.logo}
          />

          <div className={authStyles.form}>
            <Text variant="bodySm" color="muted" align="center" className={authStyles.description}>
              {copy.success}
            </Text>

            <Link to="/login">
              <Button type="button" width="full">
                Sign In
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={authStyles.container}>
      <div className={authStyles.card}>
        <img
          src="/images/orchard-offers.svg"
          alt="Orchard Cash Offer"
          className={authStyles.logo}
        />

        <form onSubmit={handleSubmit} className={authStyles.form}>
          {error && (
            <Text variant="bodySm" color="error" align="center" className={authStyles.error}>
              {error}
            </Text>
          )}

          <Text variant="bodyLg" weight="bold" align="center" className={authStyles.title}>
            {copy.title}
          </Text>
          <Text variant="bodySm" color="muted" align="center" className={authStyles.description}>
            {copy.description}
          </Text>

          <div>
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isSubmitting}
              error={!!error && !password}
              autoComplete="new-password"
            />
            <PasswordStrengthIndicator password={password} />
          </div>

          <div>
            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={isSubmitting}
              error={(!!error && !confirmPassword) || (confirmPassword.length > 0 && !passwordsMatch)}
              autoComplete="new-password"
            />
            {confirmPassword && !passwordsMatch && (
              <div className={styles.fieldError}>Passwords do not match</div>
            )}
          </div>

          <Button
            type="submit"
            width="full"
            disabled={isSubmitting || !isValid}
          >
            {isSubmitting ? copy.submitting : copy.button}
          </Button>
        </form>
      </div>
    </div>
  )
}
